import React, { MouseEvent } from 'react';
import {
  Badges,
  InfoBox,
  InfoBoxSize,
  InfoBoxVariant,
  Typography,
  TypographyVariant,
  useTheme,
} from '@secretescapes/falcon';
import { i18n } from '@secretescapes/i18n';
import { useTranslations } from '@utils/useTranslations';
import { ClickableInfoBox } from '@features/AvailabilityPresentation/Offers/OfferCard/OfferCard.styles';
import { InfoBoxType } from '@features/AvailabilityPresentation/Offers/Offers.utils';

interface InfoBoxProps {
  dataTestId: string;
  title: string;
  translationArgs?: string;
  variant?: InfoBoxVariant;
  className?: string;
}

type StringKeyInfoBoxProps = Record<InfoBoxType, InfoBoxProps>;

const infoBoxProps: StringKeyInfoBoxProps = {
  [InfoBoxType.UNAVAILABLE_FOR_DATES]: {
    dataTestId: 'unavailable-for-dates',
    title: 'offer.unavailable.change.dates',
    className: 'unavailable-message',
    variant: InfoBoxVariant.safety,
  },
  [InfoBoxType.NOT_ENOUGH_ROOMS]: {
    dataTestId: 'not-enough-rooms',
    title: 'sale.room.unavailability',
    className: 'unavailable-message',
  },
  [InfoBoxType.LOW_ROOMS]: {
    dataTestId: 'low-rooms',
    title: 'urgency.remaining.rooms.label',
  },
  [InfoBoxType.UNSUITABLE_ROOM_FOR_TRAVELLERS]: {
    dataTestId: 'unsuitable-room-for-travellers',
    title: 'offer.unsuitable.room.travellers',
    className: 'unavailable-message',
  },
};

function getInfoBoxProps(infoBoxType: InfoBoxType | null, availableRoomCount?: number) {
  if (!infoBoxType) {
    return null;
  }
  const boxProp = infoBoxProps[infoBoxType];
  if (infoBoxType === InfoBoxType.LOW_ROOMS && availableRoomCount) {
    boxProp.translationArgs = availableRoomCount.toString();
  }
  return boxProp;
}

interface Props {
  infoBoxType: InfoBoxType | null;
  availableRoomCount?: number;
  onUnavailableForDatesClick?: () => void;
  onUnsuitableForTravellersClick?: () => void;
}

export const OfferCardInfo = ({
  infoBoxType,
  availableRoomCount,
  onUnavailableForDatesClick,
  onUnsuitableForTravellersClick,
}: Props) => {
  const theme = useTheme();
  const { translations } = useTranslations();

  const boxProps = getInfoBoxProps(infoBoxType, availableRoomCount);

  const handleClick = (event: MouseEvent<HTMLDivElement>, onClickFn?: () => void) => {
    event.preventDefault();
    onClickFn?.();
  };

  if (!boxProps) {
    return null;
  }

  if (infoBoxType === InfoBoxType.LOW_ROOMS) {
    return (
      <Badges
        dataAttributes={{ testid: boxProps.dataTestId }}
        backgroundColor={theme.color.safety05}
      >
        <Typography variant={TypographyVariant.body} color={theme.color.safety60}>
          {i18n(
            boxProps.title,
            boxProps.translationArgs ? [boxProps.translationArgs] : [],
            translations
          )}
        </Typography>
      </Badges>
    );
  }

  const infoBox = (
    <InfoBox
      dataAttributes={{ testid: boxProps.dataTestId }}
      size={InfoBoxSize.small}
      subTitle={i18n(
        boxProps.title,
        boxProps.translationArgs ? [boxProps.translationArgs] : [],
        translations
      )}
      className={boxProps.className}
      variant={boxProps.variant}
    />
  );

  if (infoBoxType === InfoBoxType.UNAVAILABLE_FOR_DATES) {
    return (
      <ClickableInfoBox
        onClick={event => handleClick(event, onUnavailableForDatesClick)}
        className="unavailable-message"
      >
        {infoBox}
      </ClickableInfoBox>
    );
  }

  if (infoBoxType === InfoBoxType.UNSUITABLE_ROOM_FOR_TRAVELLERS) {
    return (
      <ClickableInfoBox
        onClick={event => handleClick(event, onUnsuitableForTravellersClick)}
        className="unavailable-message"
      >
        {infoBox}
      </ClickableInfoBox>
    );
  }

  return infoBox;
};
